import { useState, useCallback, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient, useInfiniteQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { shouldSkipInvalidation } from '@/lib/core/visibility-tracker';
import {
  SocialMessage,
  UseSocialMessagesResult
} from '@/types/social';
import { toast } from 'sonner';

const PAGE_SIZE = 50;

export function useSocialMessages(
  businessId: string | null,
  contactId: string | null
): UseSocialMessagesResult {
  const queryClient = useQueryClient();

  const queryKey = ['social-messages', businessId, contactId];

  // Fetch messages with infinite scroll
  const {
    data,
    isLoading,
    error,
    fetchNextPage,
    hasNextPage,
    refetch,
  } = useInfiniteQuery({
    queryKey,
    queryFn: async ({ pageParam = 0 }) => {
      if (!businessId || !contactId) return { messages: [], nextPage: null };

      const { data: messages, error } = await supabase
        .from('social_messages')
        .select('*')
        .eq('contact_id', contactId)
        .order('sent_at', { ascending: false })
        .range(pageParam, pageParam + PAGE_SIZE - 1);

      if (error) throw error;

      return {
        messages: (messages || []) as SocialMessage[],
        nextPage: messages && messages.length === PAGE_SIZE ? pageParam + PAGE_SIZE : null,
      };
    },
    getNextPageParam: (lastPage) => lastPage.nextPage,
    enabled: !!businessId && !!contactId,
    initialPageParam: 0,
  });

  // Flatten and reverse messages (oldest first for display)
  const messages = (data?.pages.flatMap(page => page.messages) || []).reverse();

  // Real-time subscription for messages
  useEffect(() => {
    if (!businessId || !contactId) return;

    const channel = supabase
      .channel(`social-messages-${contactId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'social_messages',
          filter: `contact_id=eq.${contactId}`,
        },
        (payload) => {
          // Skip invalidation if tab just became visible (prevents refetch storm)
          if (shouldSkipInvalidation()) return;

          if (payload.eventType === 'INSERT') {
            queryClient.invalidateQueries({ queryKey });
          } else if (payload.eventType === 'UPDATE') {
            queryClient.invalidateQueries({ queryKey });
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [businessId, contactId, queryClient, queryKey]);

  // Send message mutation
  const sendMessageMutation = useMutation({
    mutationFn: async (content: string) => {
      if (!businessId || !contactId) throw new Error('No contact selected');

      const response = await supabase.functions.invoke('social-send-message', {
        body: {
          business_id: businessId,
          contact_id: contactId,
          content,
        },
      });

      if (response.error) throw response.error;
      if (response.data?.error) throw new Error(response.data.error);

      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
      queryClient.invalidateQueries({ queryKey: ['social-inbox', businessId] });
    },
    onError: (error: Error) => {
      toast.error('Failed to send message', { description: error.message });
    },
  });

  // Mark messages as read
  const markAsReadMutation = useMutation({
    mutationFn: async () => {
      if (!contactId) return;

      const { error } = await supabase
        .from('social_messages')
        .update({ read_at: new Date().toISOString() })
        .eq('contact_id', contactId)
        .eq('direction', 'inbound')
        .is('read_at', null);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['social-inbox', businessId] });
    },
  });

  const sendMessage = useCallback(async (content: string) => {
    await sendMessageMutation.mutateAsync(content);
  }, [sendMessageMutation]);

  const markAsRead = useCallback(async () => {
    await markAsReadMutation.mutateAsync();
  }, [markAsReadMutation]);

  const loadMore = useCallback(() => {
    if (hasNextPage) {
      fetchNextPage();
    }
  }, [hasNextPage, fetchNextPage]);

  return {
    messages,
    isLoading,
    error: error as Error | null,
    hasMore: !!hasNextPage,
    loadMore,
    sendMessage,
    markAsRead,
    refresh: refetch,
  };
}

export default useSocialMessages;
